import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  TextField,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { attendanceApi, type AttendanceStatus } from '../../../lib/api/attendance.api';
import { errorText } from '../../../lib/error-text';

// ─── Los dos diálogos de asistencia ──────────────────────────────────────────
//
// VIVÍAN DENTRO DE `lms-admin.page.tsx`, al final de sus 2.900 líneas, y ahora
// los usan DOS pantallas: el cronograma —donde estaban— y la pestaña de
// Asistencia. Se extraen en vez de copiarse: el marcado es un solo acto y
// duplicarlo garantizaba que las dos copias se separaran.
//
// EL DIÁLOGO DE MARCADO NO SE TOCÓ. Ya trae el camino rápido —a quien no está
// marcado lo da por PRESENTE, y hay botón de «todos presentes»— así que
// guardar es un clic. El problema medido no era marcar, era LLEGAR.

/** Las etiquetas viven como CLAVES: el diálogo lo abre un docente que puede
 *  tener la aplicación en inglés, y «Presente» escrito a mano no cambiaría. */
const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  present: 'attendanceDialog.status.present',
  absent: 'attendanceDialog.status.absent',
  late: 'attendanceDialog.status.late',
  excused: 'attendanceDialog.status.excused',
};
/** Desde cuántos alumnos aparece el buscador. Con menos, la lista se recorre
 *  con la vista y un campo más sería ruido. */
const BUSCADOR_DESDE = 25;

const ATTENDANCE_ORDER: AttendanceStatus[] = ['present', 'late', 'absent', 'excused'];

export function AttendanceRosterDialog({
  tenantId,
  moduleId,
  date,
  onClose,
  onSaved,
}: {
  tenantId: string;
  moduleId: string;
  date: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [roster, setRoster] = useState<
    { studentId: string; name: string; status: AttendanceStatus }[]
  >([]);
  const [dayTitle, setDayTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    attendanceApi
      .getDayAttendance({ tenantId, moduleId, date })
      .then((res) => {
        if (cancelled) return;
        setDayTitle(res.topics.join(' · '));
        setRoster(
          res.roster.map((r) => ({
            studentId: r.studentId,
            name: r.name,
            // Default an unmarked student to "present" so "todos presentes" is the
            // fast path; the teacher flips the exceptions.
            status: r.status === 'none' ? 'present' : r.status,
          })),
        );
      })
      .catch((e: unknown) => setErr(errorText(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId, moduleId, date]);

  // FILTRAR NO DESMARCA: lo escrito en `roster` se guarda entero, mire quien
  // mire qué. Si el filtro recortara lo que se envía, buscar a un alumno
  // borraría la asistencia de los demás — un desastre silencioso.
  const visibles = filtro.trim()
    ? roster.filter((r) => r.name.toLowerCase().includes(filtro.trim().toLowerCase()))
    : roster;

  const setStatus = (studentId: string, status: AttendanceStatus) =>
    setRoster((prev) => prev.map((r) => (r.studentId === studentId ? { ...r, status } : r)));
  const allPresent = () =>
    setRoster((prev) => prev.map((r) => ({ ...r, status: 'present' as AttendanceStatus })));

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      await attendanceApi.markDayAttendance({
        tenantId,
        moduleId,
        date,
        marks: roster.map((r) => ({ studentId: r.studentId, status: r.status })),
      });
      onSaved();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const presentCount = roster.filter((r) => r.status === 'present' || r.status === 'late').length;

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('attendanceDialog.title')}
        <Typography variant="caption" color="text.secondary" display="block">
          {date}
          {dayTitle ? ` · ${dayTitle}` : ''} ·{' '}
          {t('attendanceDialog.presentCount', { count: presentCount, total: roster.length })}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {err && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {err}
          </Alert>
        )}
        {loading ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={20} />
          </Box>
        ) : roster.length === 0 ? (
          <Typography color="text.secondary">{t('attendanceDialog.noStudentsSection')}</Typography>
        ) : (
          <Stack spacing={1}>
            {/* FIJO ARRIBA, no dentro de lo que se desplaza. Con veintiún
                alumnos daba igual; con cincuenta, «marcar todos presentes»
                desaparecía al bajar a la mitad de la lista — justo el botón
                que hace rápido el trabajo. El contador acompaña por la misma
                razón: saber cuántos llevas sin volver al título. */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                bgcolor: 'background.paper',
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button size="small" onClick={allPresent}>
                {t('attendanceDialog.allPresent')}
              </Button>
              {/* EL BUSCADOR APARECE SÓLO CUANDO HACE FALTA. Con una lista que
                  se recorre con la vista sería ruido; pasado ese tamaño, es la
                  única forma de encontrar a alguien sin desplazarse a ciegas. */}
              {roster.length > BUSCADOR_DESDE && (
                <TextField
                  size="small"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder={t('attendanceDialog.search')}
                  sx={{ flex: 1, minWidth: 180 }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {t('attendanceDialog.presentCount', {
                  count: presentCount,
                  total: roster.length,
                })}
              </Typography>
            </Stack>
            {visibles.length === 0 && (
              <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                {t('attendanceDialog.noMatches')}
              </Typography>
            )}
            {visibles.map((r) => (
              <Stack
                key={r.studentId}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 0.5 }}
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                  {r.name}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {ATTENDANCE_ORDER.map((st) => (
                    <Chip
                      key={st}
                      label={t(ATTENDANCE_LABEL[st])}
                      size="small"
                      variant={r.status === st ? 'filled' : 'outlined'}
                      color={
                        r.status === st
                          ? st === 'present'
                            ? 'success'
                            : st === 'late'
                              ? 'warning'
                              : st === 'excused'
                                ? 'info'
                                : 'error'
                          : 'default'
                      }
                      onClick={() => setStatus(r.studentId, st)}
                      sx={{ cursor: 'pointer', height: 22 }}
                    />
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common:cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || loading || roster.length === 0}
        >
          {saving ? t('attendanceDialog.saving') : t('attendanceDialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Attendance matrix dialog (students × class days) + CSV export ───────────

const MATRIX_ABBR: Record<string, { label: string; color: string }> = {
  present: { label: 'P', color: '#34d399' },
  late: { label: 'T', color: '#fbbf24' },
  absent: { label: 'A', color: '#f87171' },
  excused: { label: 'J', color: '#60a5fa' },
  none: { label: '·', color: '#6e7681' },
};

export function AttendanceMatrixDialog({
  tenantId,
  sectionId,
  onClose,
}: {
  tenantId: string;
  sectionId: string;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const [data, setData] = useState<{
    days: { moduleId: string; date: string }[];
    students: { studentId: string; name: string; statuses: string[] }[];
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    attendanceApi
      .getSectionAttendanceMatrix({ tenantId, sectionId })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: unknown) => setErr(errorText(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId, sectionId]);

  const exportCsv = () => {
    if (!data) return;
    const header = [t('attendanceDialog.csvStudent'), ...data.days.map((d) => d.date)].join(',');
    const rows = data.students.map((s) =>
      [s.name.replace(/,/g, ''), ...s.statuses.map((st) => MATRIX_ABBR[st]?.label ?? st)].join(','),
    );
    const csv = 'data:text/csv;charset=utf-8,﻿' + [header, ...rows].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', `asistencia_${sectionId}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t('attendanceDialog.matrixTitle')}
        <Typography variant="caption" color="text.secondary" display="block">
          P Presente · T Tarde · A Ausente · J Justificado · · sin registro
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box textAlign="center" py={3}>
            <CircularProgress size={20} />
          </Box>
        ) : err ? (
          <Alert severity="error">{err}</Alert>
        ) : !data || data.students.length === 0 ? (
          <Typography color="text.secondary">{t('attendanceDialog.noStudents')}</Typography>
        ) : data.days.length === 0 ? (
          <Typography color="text.secondary">{t('attendanceDialog.noDays')}</Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    sx={{
                      textAlign: 'left',
                      p: 0.75,
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    Alumno
                  </Box>
                  {data.days.map((d) => (
                    <Box
                      component="th"
                      key={`${d.moduleId}__${d.date}`}
                      sx={{
                        p: 0.5,
                        whiteSpace: 'nowrap',
                        fontSize: 10,
                        color: 'text.secondary',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {d.date.slice(5)}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {data.students.map((s) => (
                  <Box component="tr" key={s.studentId}>
                    <Box
                      component="td"
                      sx={{
                        p: 0.75,
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        left: 0,
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {s.name}
                    </Box>
                    {s.statuses.map((st, i) => (
                      <Box
                        component="td"
                        key={i}
                        align="center"
                        sx={{
                          p: 0.5,
                          textAlign: 'center',
                          fontWeight: 700,
                          color: MATRIX_ABBR[st]?.color ?? '#6e7681',
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {MATRIX_ABBR[st]?.label ?? st}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={exportCsv} disabled={!data || data.students.length === 0}>
          {t('attendanceDialog.exportCsv')}
        </Button>
        <Button onClick={onClose}>{t('common:close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
